import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Card, Stack, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';

// ----------------------------------------------------------------------

function NavCard({ post, direction }) {
  if (!post) {
    return null;
  }

  const isNext = direction === 'next';

  return (
    <Card
      component={RouterLink}
      to={`/blog/${post.slug}`}
      sx={{
        display: 'flex',
        gap: 1.5,
        p: 2,
        flex: 1,
        minWidth: 0,
        textDecoration: 'none',
        color: 'inherit',
        textAlign: isNext ? 'right' : 'left',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '@media (hover: hover)': {
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: (theme) => `0 12px 28px ${alpha(theme.palette.grey[900], 0.14)}`,
            '& .nav-card-title': { color: 'primary.main' },
          },
        },
      }}
    >
      {!isNext && (
        <Iconify icon="eva:arrow-back-outline" sx={{ width: 20, height: 20, color: 'text.disabled', mt: 0.5 }} />
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="overline"
          sx={{ color: 'text.disabled', fontWeight: 700, letterSpacing: 1, fontSize: 10.5 }}
        >
          {isNext ? 'Next article' : 'Previous article'}
        </Typography>

        <Typography
          className="nav-card-title"
          variant="body2"
          sx={{
            color: 'text.primary',
            fontWeight: 600,
            lineHeight: 1.45,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            transition: 'color 0.2s ease',
          }}
        >
          {post.title}
        </Typography>

        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {post.category}{post.readTime ? ` · ${post.readTime} min read` : ''}
        </Typography>
      </Box>

      {isNext && (
        <Iconify icon="eva:arrow-forward-outline" sx={{ width: 20, height: 20, color: 'text.disabled', mt: 0.5 }} />
      )}
    </Card>
  );
}

NavCard.propTypes = {
  post: PropTypes.object,
  direction: PropTypes.oneOf(['previous', 'next']).isRequired,
};

// ----------------------------------------------------------------------

// Previous / next navigation so a reader who lands on one article from search
// has an obvious path to the rest of the catalogue. Read time renders only
// when the payload provides it (list rows omit it), so the caption never
// shows "undefined min read".
export default function BlogPostNav({ previous, next }) {
  if (!previous && !next) {
    return null;
  }

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      sx={{ mt: { xs: 4, md: 5 }, alignItems: 'stretch' }}
    >
      <Box sx={{ display: 'flex', flex: 1, minWidth: 0 }}>
        <NavCard post={previous} direction="previous" />
      </Box>
      <Box sx={{ display: 'flex', flex: 1, minWidth: 0 }}>
        <NavCard post={next} direction="next" />
      </Box>
    </Stack>
  );
}

BlogPostNav.propTypes = {
  previous: PropTypes.object,
  next: PropTypes.object,
};